import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Icon } from "../components/Icon.tsx";
import { Empty, ErrorView, Field, Loading, Page, Section, Sheet, useAddParam, useFail, useToast } from "../components/ui.tsx";
import { api, ApiError } from "../lib/api.ts";
import { label, when } from "../lib/format.ts";
import { useMe } from "../lib/session.tsx";
import { ListingThumb } from "./Listings.tsx";
import { DocSheet } from "./Person.tsx";

export function Properties() {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [listed, setListed] = useState(false);
  const [adding, setAdding] = useState(false);
  useAddParam(() => setAdding(true));
  useEffect(() => { const t = setTimeout(() => setDq(q), 250); return () => clearTimeout(t); }, [q]);
  const r = useQuery({ queryKey: ["properties", dq, listed], queryFn: () => api.get(`/properties?limit=150${dq ? `&q=${encodeURIComponent(dq)}` : ""}${listed ? "&listed=1" : ""}`) });
  return (
    <Page title="Properties" actions={<button className="iconbtn" aria-label="Add a property" onClick={() => setAdding(true)}><Icon name="plus" /></button>}>
      <div className="search"><Icon name="search" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Street or suburb" type="search" aria-label="Search properties" /></div>
      <div className="chips"><button className={"chip" + (listed ? " on" : "")} onClick={() => setListed(!listed)}>Listed in Rex</button></div>
      {r.isLoading ? <Loading /> : r.error ? <ErrorView error={r.error} /> : r.data.rows.length === 0 ? <Empty>No properties match.</Empty> : (
        <div className="list">
          {r.data.rows.map((p: any) => (
            <Link key={p.id} to={`/properties/${p.id}`} className="row">
              <Icon name="home" className="muted" />
              <div className="main-col"><div className="title">{p.address_display}</div>
                <div className="sub">{[p.property_type, p.beds && `${p.beds} bed`, p.people].filter(Boolean).join(" · ")}</div></div>
              {p.listing_state && <span className={"badge" + (p.listing_state.startsWith("current") ? " blue" : "")}>{p.listing_state.replace("current", "on market")}</span>}
            </Link>
          ))}
        </div>
      )}
      {adding && <AddPropertySheet onClose={() => setAdding(false)} />}
    </Page>
  );
}

function AddPropertySheet({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const fail = useFail();
  const [f, setF] = useState({ street_number: "", street_name: "", suburb: "", postcode: "", unit: "" });
  const save = async () => {
    try { const p: any = await api.post("/properties", { ...f, unit: f.unit || null, postcode: f.postcode || null }); nav(`/properties/${p.id}`); }
    catch (e) { if (e instanceof ApiError && e.code === "exists") { onClose(); nav(`/properties/${e.details.property_id}`); } else fail(e); }
  };
  return (
    <Sheet title="Add a property" onClose={onClose} foot={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn" disabled={!f.suburb || !f.street_name} onClick={save}>Add</button></>}>
      <div className="grid2">
        <Field label="Unit"><input className="input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></Field>
        <Field label="Number"><input className="input" value={f.street_number} onChange={(e) => setF({ ...f, street_number: e.target.value })} /></Field>
      </div>
      <Field label="Street"><input className="input" value={f.street_name} onChange={(e) => setF({ ...f, street_name: e.target.value })} placeholder="Banksia Street" /></Field>
      <div className="grid2">
        <Field label="Suburb"><input className="input" value={f.suburb} onChange={(e) => setF({ ...f, suburb: e.target.value })} /></Field>
        <Field label="Postcode"><input className="input" inputMode="numeric" value={f.postcode} onChange={(e) => setF({ ...f, postcode: e.target.value.replace(/\D/g, "").slice(0, 4) })} /></Field>
      </div>
      <div className="muted" style={{ fontSize: 13 }}>If the address is already on file, you'll be taken to it instead.</div>
    </Sheet>
  );
}

export function Property() {
  const { id } = useParams();
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const [sheet, setSheet] = useState<null | "doc" | "link" | "deal">(null);
  const q = useQuery({ queryKey: ["property", id], queryFn: () => api.get(`/properties/${id}`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["property", id] });
  if (q.isLoading) return <Page title="Property" back><Loading /></Page>;
  if (q.error || !q.data) return <Page title="Property" back><ErrorView error={q.error} /></Page>;
  const p = q.data;
  const download = async (docId: string) => { try { const r: any = await api.get(`/documents/${docId}/download`); window.open(r.url, "_blank", "noopener"); } catch (e) { fail(e); } };
  return (
    <Page title={p.address_display} back quiet>
      <div className="record-head"><div className="avatar"><Icon name="home" /></div>
        <div><h2>{p.address_display}</h2><div className="meta">{[p.property_type, p.beds && `${p.beds} bed`, p.baths && `${p.baths} bath`, p.cars && `${p.cars} car`, p.land_area_m2 && `${Number(p.land_area_m2).toLocaleString()} m²`].filter(Boolean).join(" · ")}</div></div>
      </div>
      <div className="actions pad" style={{ marginTop: 8 }}>
        <button className="btn ghost sm" onClick={() => setSheet("link")}><Icon name="user" />Link a person</button>
        <button className="btn ghost sm" onClick={() => setSheet("deal")}><Icon name="pipeline" />Seller deal</button>
        <button className="btn ghost sm" onClick={() => setSheet("doc")}><Icon name="doc" />Upload</button>
      </div>
      <Section title="People" count={p.people.length}>
        {p.people.length === 0 ? <Empty>No one you can see is linked.</Empty> : (
          <div className="list">{p.people.map((pp: any) => (
            <Link key={pp.id} to={`/people/${pp.person_id}`} className="row"><div className="main-col"><div className="title">{pp.display_name}</div><div className="sub">{pp.role}{pp.to_date ? " (past)" : ""}</div></div><Icon name="chevron" className="chev" /></Link>
          ))}</div>
        )}
      </Section>
      <Section title="Rex listing" count={p.listings.length}>
        {p.listings.length === 0 ? <Empty>Not listed in Rex.</Empty> : (
          <div className="list">{p.listings.map((l: any) => (
            <Link key={l.id} to={`/listings/${l.id}`} className="row"><ListingThumb photo={l.photo} />
              <div className="main-col"><div className="title">{l.headline}</div><div className="sub">{l.state}{l.under_contract ? ", under contract" : ""} · {l.price_advertise_as} · {l.interested} interested</div></div>
              <Icon name="chevron" className="chev" /></Link>
          ))}</div>
        )}
      </Section>
      <Section title="Enquiries" count={p.enquiries.length}>
        {p.enquiries.length === 0 ? <Empty>None you can see.</Empty> : (
          <div className="list">{p.enquiries.map((e: any) => (
            <Link key={e.id} to={`/people/${e.person_id}`} className="row"><div className="main-col"><div className="title">{e.display_name}</div><div className="sub">{label(e.status)}{e.interest_level ? ` · ${label(e.interest_level)}` : ""}</div></div><span className="when">{when(e.first_enquiry_at)}</span></Link>
          ))}</div>
        )}
      </Section>
      <Section title="Deals" count={p.deals.length}>
        {p.deals.length === 0 ? <Empty>None.</Empty> : <div className="list">{p.deals.map((d: any) => (
          <Link key={d.id} to={`/pipelines?pipeline=${d.pipeline}&open=${d.id}`} className="row"><div className="main-col"><div className="title">{d.title}</div><div className="sub">{d.stage}</div></div></Link>))}</div>}
      </Section>
      <Section title="Notes" count={p.notes.length}>
        {p.notes.length === 0 ? <Empty>No notes.</Empty> : <div className="timeline">{p.notes.map((n: any) => (
          <div key={n.id} className="tl"><div className="ic"><Icon name="note" /></div><div><div className="hd"><b style={{ color: "var(--ink)" }}>{label(n.kind) || "Note"}</b><span>{when(n.occurred_at)}</span><span>{n.author}</span>{n.visibility === "owner_admin" && <span className="badge">Private</span>}</div><div className="bd">{n.body}</div></div></div>))}</div>}
      </Section>
      <Section title="Documents" count={p.documents.length}>
        {p.documents.length === 0 ? <Empty>None you can see.</Empty> : <div className="list">{p.documents.map((d: any) => (
          <button key={d.id} className="row" onClick={() => download(d.id)}><Icon name="doc" className="muted" /><div className="main-col"><div className="title">{d.title}</div><div className="sub">{d.kind.replace(/_/g, " ")}</div></div></button>))}</div>}
      </Section>
      {sheet === "doc" && <DocSheet target={{ property_id: p.id }} canRestrict={me.user.role !== "agent"} onClose={() => setSheet(null)} onDone={refresh} />}
      {sheet === "link" && <LinkPersonSheet propertyId={p.id} onClose={() => setSheet(null)} onDone={() => { refresh(); toast("Linked"); }} />}
      {sheet === "deal" && <SellerDealSheet property={p} onClose={() => setSheet(null)} onDone={() => { refresh(); toast("Seller deal created"); }} />}
    </Page>
  );
}

function LinkPersonSheet({ propertyId, onClose, onDone }: { propertyId: string; onClose: () => void; onDone: () => void }) {
  const fail = useFail();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("owner");
  const people = useQuery({ queryKey: ["people-pick", q], queryFn: () => api.get(`/people?q=${encodeURIComponent(q)}&limit=8`), enabled: q.length >= 2 });
  const link = async (personId: string) => { try { await api.post(`/properties/${propertyId}/people`, { person_id: personId, role }); onDone(); onClose(); } catch (e) { fail(e); } };
  return (
    <Sheet title="Link a person" onClose={onClose}>
      <Field label="As"><select className="select" value={role} onChange={(e) => setRole(e.target.value)}>{["owner", "vendor", "purchaser", "interested", "tenant", "landlord", "solicitor", "contractor", "occupant"].map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
      <Field label="Who"><input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" /></Field>
      {(people.data?.rows ?? []).map((p: any) => <button key={p.id} className="row" onClick={() => link(p.id)}><div className="main-col"><div className="title">{p.display_name}</div></div><Icon name="plus" /></button>)}
    </Sheet>
  );
}

function SellerDealSheet({ property, onClose, onDone }: { property: any; onClose: () => void; onDone: () => void }) {
  const fail = useFail();
  const owners = property.people.filter((p: any) => ["owner", "vendor"].includes(p.role));
  const [personId, setPersonId] = useState(owners[0]?.person_id ?? "");
  const [value, setValue] = useState("");
  const save = async () => {
    try { await api.post("/opportunities", { pipeline: "seller", person_id: personId, property_id: property.id, value_cents: value ? Number(value) * 100 : undefined }); onDone(); onClose(); } catch (e) { fail(e); }
  };
  return (
    <Sheet title="New seller deal" onClose={onClose} foot={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn" disabled={!personId} onClick={save}>Create</button></>}>
      {owners.length === 0 ? <div className="banner" style={{ margin: "0 0 12px" }}>Link the owner to this property first.</div> : (
        <Field label="Owner"><select className="select" value={personId} onChange={(e) => setPersonId(e.target.value)}>{owners.map((o: any) => <option key={o.person_id} value={o.person_id}>{o.display_name}</option>)}</select></Field>
      )}
      <Field label="Expected price ($)"><input className="input" inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))} /></Field>
    </Sheet>
  );
}

